import React, { useEffect, useState } from "react";
import { api, openAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Repeat, Search, Loader2, Copy, CheckCircle2, KeyRound, Printer, Users } from "lucide-react";
import { toast } from "sonner";

export default function ScolariteInscriptionsPage() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openRe, setOpenRe] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", birth_date: "", gender: "M",
    class_name: "", academic_year: "2026-2027",
    parent_first_name: "", parent_last_name: "", parent_phone: "", parent_email: "",
    previous_school: "", medical_notes: "", address: "",
  });
  const [reForm, setReForm] = useState({ new_class_name: "", new_academic_year: "2026-2027", notes: "" });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [classFilter, setClassFilter] = useState("all");
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = async () => {
    try {
      const params = { q };
      if (classFilter !== "all") params.class_name = classFilter;
      const { data } = await api.get("/scolarite/students-search", { params });
      setRows(data);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); api.get("/classes").then(({ data }) => setClasses(data)).catch(() => {}); }, [classFilter]);

  const createAccount = async (s) => {
    try {
      const { data } = await api.post(`/scolarite/students/${s.id}/create-account`);
      toast.success(data.message || "Compte créé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const bulkAccounts = async () => {
    const scope = classFilter !== "all" ? `de la classe ${classFilter}` : "de TOUS les élèves";
    if (!window.confirm(`Activer les comptes ${scope} ? (identifiant et mot de passe initial = matricule)`)) return;
    setBulkLoading(true);
    try {
      const payload = classFilter !== "all" ? { class_name: classFilter } : {};
      const { data } = await api.post("/scolarite/student-accounts/bulk", payload);
      toast.success(`${data.created} compte(s) créé(s) · ${data.already_active} déjà actif(s)${data.no_matricule ? ` · ${data.no_matricule} sans matricule` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBulkLoading(false); }
  };

  const printCredentials = () => {
    const qs = classFilter !== "all" ? `?class_name=${encodeURIComponent(classFilter)}` : "";
    openAuthedFile(`/scolarite/student-accounts/credentials-pdf${qs}`).catch(() => toast.error("Impossible de générer le PDF"));
  };

  const submitNew = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.birth_date || !form.class_name) {
      return toast.error("Nom, prénom, naissance et classe obligatoires");
    }
    setSaving(true);
    try {
      const { data } = await api.post("/scolarite/enrollments", form);
      setResult(data);
      toast.success(`Élève créé — matricule ${data.student.matricule}`);
      setForm({ ...form, first_name: "", last_name: "", birth_date: "", parent_email: "", parent_phone: "" });
      setOpenNew(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const submitReinscription = async () => {
    if (!openRe || !reForm.new_class_name) return toast.error("Classe requise");
    setSaving(true);
    try {
      await api.post("/scolarite/reinscriptions", { student_id: openRe.id, ...reForm });
      toast.success("Réinscription enregistrée");
      setOpenRe(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="scolarite-inscriptions">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><UserPlus className="h-3 w-3" /><span>INSCRIPTIONS & RÉINSCRIPTIONS</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Gérer les élèves</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={printCredentials} className="gap-1 border-[#1E3A8A] text-[#1E3A8A] hover:bg-blue-50" data-testid="print-credentials-btn">
            <Printer className="h-4 w-4" />Imprimer identifiants
          </Button>
          <Button variant="outline" onClick={bulkAccounts} disabled={bulkLoading} className="gap-1 border-[#166534] text-[#166534] hover:bg-green-50" data-testid="bulk-accounts-btn">
            {bulkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}Activer les comptes
          </Button>
          <Button onClick={() => setOpenNew(true)} className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="new-enroll-btn">
            <UserPlus className="h-4 w-4" />Nouvelle inscription
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher matricule / nom..." className="flex-1 min-w-[220px]" data-testid="search-input" />
        <Select value={classFilter} onValueChange={setClassFilter}>
          <SelectTrigger className="w-52" data-testid="class-filter"><SelectValue placeholder="Toutes les classes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les classes</SelectItem>
            {classes.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load}><Search className="h-4 w-4 mr-1" />Chercher</Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Matricule</th>
                <th className="px-4 py-3 text-left">Nom complet</th>
                <th className="px-4 py-3 text-left">Classe</th>
                <th className="px-4 py-3 text-left">Année</th>
                <th className="px-4 py-3 text-center">Compte élève</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`student-${s.id}`}>
                  <td className="px-4 py-2 font-mono text-xs text-[#1E3A8A]">{s.matricule}</td>
                  <td className="px-4 py-2 font-medium">{s.first_name} {s.last_name}</td>
                  <td className="px-4 py-2">{s.class_name}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{s.academic_year || "—"}</td>
                  <td className="px-4 py-2 text-center">
                    {s.user_id ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-green-100 text-green-800" data-testid={`account-active-${s.id}`}>
                        <CheckCircle2 className="h-3 w-3" />Actif
                      </span>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => createAccount(s)} className="gap-1 text-xs border-[#166534] text-[#166534] hover:bg-green-50" data-testid={`create-account-${s.id}`}>
                        <KeyRound className="h-3 w-3" />Créer compte
                      </Button>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => { setOpenRe(s); setReForm({ new_class_name: "", new_academic_year: "2026-2027", notes: "" }); }} className="gap-1" data-testid={`re-${s.id}`}>
                      <Repeat className="h-3 w-3" />Réinscrire
                    </Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Aucun élève</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Result dialog for new enrollment */}
      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Inscription réussie ✅</DialogTitle></DialogHeader>
          {result && (
            <div className="space-y-3 text-sm">
              <div className="bg-green-50 border border-green-300 rounded-lg p-3">
                <div className="text-xs text-slate-500">Matricule (identifiant à vie)</div>
                <div className="font-mono font-bold text-lg text-[#1E3A8A]">{result.student.matricule}</div>
              </div>
              <div><strong>Élève :</strong> {result.student.first_name} {result.student.last_name} — {result.student.class_name}</div>
              {result.parent_created && (
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                  <div className="font-semibold text-yellow-900">Compte parent créé</div>
                  <div className="text-xs mt-1">Le parent recevra un email d&apos;activation. Mot de passe temporaire :</div>
                  <code className="block bg-white px-2 py-1 rounded mt-1 font-mono">{result.temp_password}</code>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New enrollment dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nouvelle inscription d&apos;élève</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="text-xs uppercase text-slate-500 font-semibold">Élève</div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Prénom *" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="e-first" />
              <Input placeholder="Nom *" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="e-last" />
              <Input type="date" value={form.birth_date} onChange={e => setForm({ ...form, birth_date: e.target.value })} data-testid="e-birth" />
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="e-gender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Masculin</SelectItem>
                  <SelectItem value="F">Féminin</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Classe *" value={form.class_name} onChange={e => setForm({ ...form, class_name: e.target.value })} list="cl" data-testid="e-class" />
              <Input placeholder="Année scolaire" value={form.academic_year} onChange={e => setForm({ ...form, academic_year: e.target.value })} data-testid="e-year" />
              <datalist id="cl">{classes.map(c => <option key={c.name} value={c.name} />)}</datalist>
            </div>
            <div className="text-xs uppercase text-slate-500 font-semibold pt-2">Parent (crée un compte si email fourni)</div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Prénom parent" value={form.parent_first_name} onChange={e => setForm({ ...form, parent_first_name: e.target.value })} data-testid="p-first" />
              <Input placeholder="Nom parent" value={form.parent_last_name} onChange={e => setForm({ ...form, parent_last_name: e.target.value })} data-testid="p-last" />
              <Input placeholder="Téléphone" value={form.parent_phone} onChange={e => setForm({ ...form, parent_phone: e.target.value })} data-testid="p-phone" />
              <Input placeholder="Email" value={form.parent_email} onChange={e => setForm({ ...form, parent_email: e.target.value })} data-testid="p-email" />
            </div>
            <Input placeholder="Ancienne école (optionnel)" value={form.previous_school} onChange={e => setForm({ ...form, previous_school: e.target.value })} data-testid="e-prev" />
            <Input placeholder="Adresse" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
            <Textarea placeholder="Notes médicales" value={form.medical_notes} onChange={e => setForm({ ...form, medical_notes: e.target.value })} rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>Annuler</Button>
            <Button onClick={submitNew} disabled={saving} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="e-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4" />Inscrire</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reinscription dialog */}
      <Dialog open={!!openRe} onOpenChange={(o) => !o && setOpenRe(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Réinscription — {openRe?.first_name} {openRe?.last_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">Classe actuelle : <strong>{openRe?.class_name}</strong></div>
            <div>
              <Label>Nouvelle classe *</Label>
              <Input value={reForm.new_class_name} onChange={e => setReForm({ ...reForm, new_class_name: e.target.value })} placeholder="Ex : 5ème A" data-testid="re-class" />
            </div>
            <div>
              <Label>Nouvelle année scolaire</Label>
              <Input value={reForm.new_academic_year} onChange={e => setReForm({ ...reForm, new_academic_year: e.target.value })} data-testid="re-year" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={reForm.notes} onChange={e => setReForm({ ...reForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenRe(null)}>Annuler</Button>
            <Button onClick={submitReinscription} disabled={saving} className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid="re-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Réinscrire"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
